import { normalizeDashes } from '../reportRendering/normalizeReportDashes';
import { stripNarrativeArtifacts } from '../reportRendering/narrativeArtifacts';
import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import { presentReportReliabilityTokens } from '../reportRendering/presentReportReliabilityTokens';
// ─── Formatting primitives ─────────────────────────────────────────────────────

export const dash = (s: string): string => normalizeDashes(s);
/** Typography only: "40.7°C" → "40.7 °C"; "1750 MW" → "1,750 MW" (grouping, never rounding). */
export const typo = (s: string): string => (s ? s
  .replace(/(\d)°C/g, '$1 °C')
  .replace(/\b(\d{4,})(?=\s?MW\b)/g, (m) => new Intl.NumberFormat('en-US').format(Number(m)))
  .replace(/\b(\d+)-(\d+)(?=\s*(?:year|month|MW|MGD|%))/g, '$1–$2') : s);
export const fmtInt = (n: number): string => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
export const fmtDec = (n: number, d = 1): string => new Intl.NumberFormat('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "29 Sep 2026" — fixed month abbreviations (ICU versions disagree on "Sept"). */
export const fmtDate = (iso: string): string => { const d = new Date(iso); return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`; };
export const fmtDateTimeUtc = (iso: string): string => `${fmtDate(iso)}, ${iso.slice(11, 16)} UTC`;
export const sentenceCase = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);
export const lowerFirst = (s: string): string => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
export const titleCaseWords = (s: string): string => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/** First sentence (the platform helper's rule: ends at . ! ? before a capital/quote/bracket, ignoring common abbreviations). */
export function firstSentence(text: string): string {
  const m = /^(.+?(?<!\b(?:No|vs|etc|approx|ca|Fig|Figs|Ref|Refs|Sec|Vol|Dr|Mr|Ms|St|Inc|Ltd|Co|Corp)\.))(?=[.!?])([.!?])(?:\s+(?=[A-Z“"(\[])|$)/.exec(text.trim());
  return (m ? `${m[1]}${m[2]}` : text).trim();
}

/**
 * condense(text, max) — the ONE prose-shortening rule.
 * 1. sentence 1 only; 2. if it fits, keep it; 3. else cut at the LAST clause
 * boundary (`;`, ` — `, ` and `, ` before `, ` to `, `, with`) whose prefix is
 * 40…max characters; 4. else hard-cut at a word boundary with an ellipsis.
 * [Ref N] tokens that fall in the kept prefix are kept.
 */
/** Like condense, but keeps only the FIRST clause (≥ 40 chars) when a boundary exists — for one-line fit notes. */
export function firstClause(text: string, max = 130): string {
  const s = firstSentence(dash(text)).replace(/\s+/g, ' ');
  for (const b of [', with ', ', but ', ', which ', '; ', ' – ']) {
    const i = s.indexOf(b);
    if (i >= 40 && !/\b(not|unknown|unverified|unconfirmed|conditional|not attested|not established)\b/i.test(s.slice(i))) return `${s.slice(0, i).replace(/[,;:\s]+$/, '')}.`;
  }
  return condense(s, max);
}

export function condense(text: string, max = 110, min = 40): string {
  const s = firstSentence(dash(text)).replace(/\s+/g, ' ');
  const bare = s.replace(/\s*\[Ref \d+\]/g, '');
  if (bare.length <= max) return s;
  const boundaries = [';', ' – ', ', but ', ', which ', ', with ', ', any ', ', complete ', ', perform ', ', conduct ', ' across ', ' prior to ', ' while ', ' and ', ' before ', ' to ', ' with '];
  let cut = -1;
  for (const b of boundaries) {
    let i = s.indexOf(b);
    while (i !== -1) {
      const prefixLen = s.slice(0, i).replace(/\s*\[Ref \d+\]/g, '').length;
      // " to " after a participle ("required to", "needed to") or " with " after ("paired with", "coupled with") is not a clause boundary.
      const participle = (b === ' to ' && /\b(required|needed|used|able|designed)$/i.test(s.slice(0, i)))
        || (b === ' with ' && /\b(paired|coupled|combined|integrated|equipped|configured|aligned|consistent|associated|along|together)$/i.test(s.slice(0, i)))
        // ", and" closes a list: cutting there leaves an unfinished list.
        || (b === ' and ' && (
          s[i - 1] === ','
          || /\b(?:boundary|water|operation|design|capacity|schedule|hydrologic|laws|codes|air|oil|state|federal|Planning|supply|rights|permits?|studies|testing|monitoring|division|Phase\s+\d+|wetland\s+delineation|\(ESA\)|chillers)$/i.test(s.slice(0, i))
          || /^(?:topographic|wastewater|maintenance|construction|rights|cost|hydraulic|regulations|standards|Development|sewer|drainage|foundations?|laramie|Phase\s+\d+|USACE|(?:a\s+)?(?:boundary-specific\s+|parcel-specific\s+|jurisdictional\s+)?wetland\s+delineation)\b/i.test(s.slice(i + 5))
        ));
      if (!participle && prefixLen >= min && prefixLen <= max && i > cut) cut = i;
      i = s.indexOf(b, i + 1);
    }
  }
  if (cut > 0) return `${s.slice(0, cut).replace(/[,;:\s]+$/, '')}.`;
  // Fallback 1: Oxford-comma 3-item list ("Item 1, Item 2, and Item 3") -> "Item 1 and Item 2."
  const oxfordIdx = s.indexOf(', and ');
  if (oxfordIdx > 0) {
    const prefix = s.slice(0, oxfordIdx);
    const prefixLen = prefix.replace(/\s*\[Ref \d+\]/g, '').length;
    if (prefixLen >= Math.max(min, 60) && prefixLen + 4 <= max) {
      let depth = 0;
      let commaPos = -1;
      for (let k = 0; k < prefix.length; k++) {
        if (prefix[k] === '(') depth++;
        else if (prefix[k] === ')') depth = Math.max(0, depth - 1);
        else if (depth === 0 && prefix[k] === ',' && prefix[k + 1] === ' ') commaPos = k;
      }
      if (commaPos > 15) {
        return `${prefix.slice(0, commaPos)} and ${prefix.slice(commaPos + 2).replace(/[,;:\s]+$/, '')}.`;
      }
    }
  }
  // Fallback 2: First item of an article-led list ("Item 1, a/an Item 2") -> "Item 1."
  for (const listSep of [', an ', ', a ']) {
    const idx = s.indexOf(listSep);
    if (idx > 0) {
      const prefixLen = s.slice(0, idx).replace(/\s*\[Ref \d+\]/g, '').length;
      if (prefixLen >= min && prefixLen <= max) {
        return `${s.slice(0, idx).replace(/[,;:\s]+$/, '')}.`;
      }
    }
  }
  const hard = s.slice(0, max - 1);
  const wordCut = hard
    .slice(0, hard.lastIndexOf(' '))
    .replace(/\s+\b(?:a|an|the|and|or|to|of|in|for|with|on|at|by|from|under|into|via|per|vs\.?)$/i, '')
    .replace(/[,;:\s]+$/, '');
  return `${wordCut}…`;
}

/** Caveat-honesty filter (sanity rule: a mapped floodplain share never "requires" fill). */
export function dropRequiredWorksClause(text: string): string {
  return text.replace(/\s+and design required elevation fill/i, '').replace(/,?\s*(?:which\s+)?(?:requires|necessitates)[^.;]*\b(?:fill|grading|elevated equipment pads)\b[^.;]*/i, '');
}

export const refsIn = (text: string): number[] => [...text.matchAll(/\[Ref (\d+)\]/g)].map((m) => Number(m[1]));

/** Legacy reviewer markers are not canonical reference bindings. In particular,
 * renumbered reports can retain `[verified Ref N]` prose whose N now names an
 * unrelated source. Disclose that gap instead of silently granting authority. */
export function presentSummaryCitations<T>(value: T, referenceCount: number, taggedNumberingProven = false): T {
  // The full report renders `[Ref N] [source passage](doc://…)` as a deep link
  // into the document reader. The Summary has no reader link, so keep the
  // [Ref N] citation and drop the internal document id and character offsets.
  if (typeof value === 'string') return presentReportReliabilityTokens(stripNarrativeArtifacts(
      value.replace(/\s*\[[^\]\n]{1,80}\]\(doc:\/\/[^)\s]*\)/g, ''),
    )
    .replace(/\s*\[source passage\]/gi, '')
    .replace(/(?:,\s*|\s+)?\(doc:\/\/(?!sim\/)[^\s)\],;"']*[^\s)\],;."']\)/gi, '')
    .replace(/(?:,\s*|\s+)?doc:\/\/(?!sim\/)[^\s)\],;"']*[^\s)\],;."']/gi, '')
    .replace(/\(\s*[,;:]\s*/g, '(')
    .replace(/\s*[,;:]\s*\)/g, ')')
    .replace(/\s*\(\s*\)/g, '')
    .replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'), marker => {
    return marker.slice(1, -1).split(/\s*,\s*/).map(member => {
      const match = /^(?:(verified|inferred|unverified)\s+)?(?:Ref\s*(?::\s*)?)?(\d+)$/i.exec(member.trim())!;
      const n = Number(match[2]);
      const label = match[1]?.toLowerCase();
      const visibleWarning = label === 'unverified' ? label : undefined;
      const canonicalLegacy = /^\[Ref \d+\]$/.test(marker);
      if ((!taggedNumberingProven && !canonicalLegacy) || n <= 0 || n > referenceCount) {
        return `(${visibleWarning ? `${visibleWarning}; ` : ''}source unresolved in saved report)`;
      }
      // Reliability classifications stay in the frozen source. Only the
      // unverified warning is visible alongside the Summary citation.
      return `${visibleWarning ? `${visibleWarning} source ` : ''}[Ref ${n}]`;
    }).join(', ');
  })
    .replace(/(\[Ref (\d+)\])(?:\s*,?\s*\1)+/g, '$1')) as T;
  if (Array.isArray(value)) return value.map(item => presentSummaryCitations(item, referenceCount, taggedNumberingProven)) as T;
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value)
    .map(([key, item]) => [key, presentSummaryCitations(item, referenceCount, taggedNumberingProven)])) as T;
  return value;
}
